import React, { useEffect, useState } from 'react';
import { Form, SpaceBetween, Spinner } from '@amzn/awsui-components-react-v3';
import {
  applyExistingRequestContextsToAdditionalMetadata,
  findExistingRequestContexts,
  TemplateMetadata,
} from 'src/components/common/checkout/common';
import {
  GetRequestContextRequest,
  GetRequestContextResponse,
  UserRequestContext,
} from 'aws-sdk/clients/awsdatalakegladstonelambda';
import { RequestContextDetail } from 'src/components/datausageagreement/requestContextDetail';
import { getRequestContext } from 'src/api/permissions';
import { OmniDUAContext } from 'src/components/datausageagreement/widgets/OmniDUAContext';

export interface DataAgreementsRequestContextEditorProps {
  activeGroup: string;
  activeWorkspace: any;

  additionalMetadata: TemplateMetadata[];
  setAdditionalMetadata: (additionalMetadata: TemplateMetadata[]) => void;

  displaySaveButton: boolean;

  setContentType: any;
  match: any;

  omniContext?: OmniDUAContext;
}

export const DataAgreementsRequestContextEditor = React.forwardRef(
  (props: DataAgreementsRequestContextEditorProps, ref: React.Ref<any>) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [agreementIdToRequestContextMap, setAgreementIdToRequestContextMap] =
      useState<Map<string, UserRequestContext>>(undefined);

    useEffect(() => {
      handleRefresh();
    }, [props.additionalMetadata]);

    useEffect(() => {
      onPageLoad();
    }, []);

    const onPageLoad = async () => {
      setLoading(true);
      // get a map from dataAgreementId to requestContextId for any existing request contexts the user has that are eligible for auto-merge.
      const agreementIdToRequestContextIdMap = await findExistingRequestContexts(props);
      // update additionalMetadata to use these request contexts: if a given ARW matches an agreementId then set the requestContextId
      props.setAdditionalMetadata(
        applyExistingRequestContextsToAdditionalMetadata(props.additionalMetadata, agreementIdToRequestContextIdMap),
      );
      setLoading(false);
    };

    const handleRefresh = async () => {
      const uniqueAgreementIds = getUniqueAgreementIds();
      const newAgreementIdToRequestContextMap = new Map<string, UserRequestContext>();
      for (const agreementId of uniqueAgreementIds) {
        const requestContextId = await getRequestContextIdForDataAgreementId(agreementId);
        const requestContext = await getRequestContextForId(requestContextId);
        newAgreementIdToRequestContextMap.set(agreementId, requestContext);
      }
      setAgreementIdToRequestContextMap(newAgreementIdToRequestContextMap);
    };

    const additionalMetadataForARWOnly = () =>
      props.additionalMetadata.filter(
        (metadata) =>
          metadata.accessRequestWorkflows?.length > 0 &&
          metadata.accessRequestWorkflows.some(
            (accessRequestWorkflow) => accessRequestWorkflow.dataAgreement != undefined,
          ),
      );

    const getUniqueAgreementIds = () => {
      const resourceToUsageAgreementsMapping = new Map<string, string[]>();
      additionalMetadataForARWOnly().forEach((metadata) => {
        const usageAgreementIds = metadata.accessRequestWorkflows?.map(
          (accessRequestWorkflow) => accessRequestWorkflow.dataAgreement?.agreementId,
        );
        if (!resourceToUsageAgreementsMapping.has(metadata.resourceId)) {
          resourceToUsageAgreementsMapping.set(metadata.resourceId, []);
        }
        let agreementIds = resourceToUsageAgreementsMapping.get(metadata.resourceId);
        if (usageAgreementIds?.length > 0) {
          agreementIds.push(...usageAgreementIds);
        }
        resourceToUsageAgreementsMapping.set(metadata.resourceId, agreementIds);
      });

      const uniqueAgreementIds = new Set<string>();
      resourceToUsageAgreementsMapping.forEach((agreementIds) => {
        agreementIds.forEach((agreementId) => {
          uniqueAgreementIds.add(agreementId);
        });
      });
      return uniqueAgreementIds;
    };

    const setRequestContext = (requestContext: UserRequestContext) => {
      let newAdditionalMetadata = JSON.parse(JSON.stringify(props.additionalMetadata)) as TemplateMetadata[];
      newAdditionalMetadata.forEach((metadata) => {
        if (metadata.accessRequestWorkflows?.length > 0) {
          metadata.accessRequestWorkflows.forEach((accessRequestWorkflow) => {
            if (accessRequestWorkflow.dataAgreement?.agreementId === requestContext.requestSchema.dataAgreementId) {
              accessRequestWorkflow.requestContextId = requestContext.requestContextId;
            }
          });
        }
      });
      props.setAdditionalMetadata(newAdditionalMetadata);
    };

    const getRequestContextIdForDataAgreementId = async (agreementId: string) => {
      let requestContextId = undefined;
      additionalMetadataForARWOnly().forEach((template) => {
        template?.accessRequestWorkflows.forEach((arw) => {
          if (arw?.dataAgreement?.agreementId == agreementId) {
            requestContextId = arw?.requestContextId;
          }
        });
      });
      return requestContextId;
    };

    const getRequestContextForId = async (requestContextId: string) => {
      if (!requestContextId) return;
      const getRequestContextRequest: GetRequestContextRequest = {
        id: requestContextId,
      };
      try {
        const getRequestContextResponse: GetRequestContextResponse = await getRequestContext(getRequestContextRequest);
        return getRequestContextResponse.userRequestContext;
      } catch (e) {
        console.error('Failed to get request context for ID: {}', requestContextId);
        console.error(e);
        return;
      }
    };

    if (loading || !agreementIdToRequestContextMap) {
      return <Spinner />;
    }

    return (
      <Form>
        <SpaceBetween size={'m'} direction={'vertical'}>
          {[...agreementIdToRequestContextMap.keys()].map((agreementId) => {
            const requestContext = agreementIdToRequestContextMap.get(agreementId);
            return (
              <RequestContextDetail
                {...props}
                ref={ref}
                displayMetadataContainer={false}
                displaySaveButton={props.displaySaveButton}
                setContentType={props.setContentType}
                match={props.match}
                activeGroup={props.activeGroup}
                dataAgreementId={agreementId}
                requestContext={requestContext}
                setRequestContext={setRequestContext}
                omniContext={props.omniContext}
              />
            );
          })}
        </SpaceBetween>
      </Form>
    );
  },
);
